import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // ============ Charger les notifications ============
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/notifications', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Erreur notifications:', err);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Rafraîchir toutes les 60 secondes
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // ============ Fermer en cliquant ailleurs ============
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============ Marquer comme lue ============
  const handleRead = async (notif) => {
    try {
      if (!notif.is_read) {
        const token = localStorage.getItem('token');
        await api.post(`/notifications/${notif.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      }

      // Rediriger si lien
      if (notif.link) {
        setOpen(false);
        navigate(notif.link);
      }
    } catch (err) {
      console.error('Erreur read:', err);
    }
  };

  // ============ Tout marquer comme lu ============
  const handleReadAll = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.post('/notifications/read-all', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Erreur read all:', err);
    }
  };

  // ============ Supprimer ============
  const handleDelete = async (e, notif) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/notifications/${notif.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      if (!notif.is_read) setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error('Erreur delete:', err);
    }
  };

  // ============ Style selon type ============
  const typeStyle = (type) => {
    const map = {
      success: 'bg-green-50 border-green-200',
      warning: 'bg-yellow-50 border-yellow-200',
      danger: 'bg-red-50 border-red-200',
      info: 'bg-blue-50 border-blue-200',
    };
    return map[type] || 'bg-gray-50 border-gray-200';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Cloche */}
      <button
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition"
        aria-label="Notifications"
      >
        <span className="text-xl">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#F39C12] text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panneau déroulant */}
      {open && (
        <div className="absolute right-0 top-12 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-bold text-[#0A0A0A]">
              Notifications {unreadCount > 0 && `(${unreadCount})`}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleReadAll}
                className="text-xs text-[#F39C12] hover:underline font-medium"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* Liste */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-gray-500 text-sm">Chargement...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="text-3xl mb-2">🔕</p>
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleRead(notif)}
                  className={`flex items-start gap-3 p-4 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition ${
                    !notif.is_read ? 'bg-orange-50/40' : ''
                  }`}
                >
                  {/* Icône */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border ${typeStyle(notif.type)}`}>
                    <span className="text-lg">{notif.icon || '📌'}</span>
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm ${!notif.is_read ? 'font-bold text-[#0A0A0A]' : 'font-medium text-gray-700'}`}>
                        {notif.title}
                      </p>
                      {!notif.is_read && (
                        <span className="w-2 h-2 rounded-full bg-[#F39C12] flex-shrink-0 mt-1.5"></span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {notif.message}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      {notif.created_at_human || notif.created_at}
                    </p>
                  </div>

                  {/* Supprimer */}
                  <button
                    onClick={(e) => handleDelete(e, notif)}
                    className="text-gray-300 hover:text-red-500 transition flex-shrink-0 text-lg"
                    title="Supprimer"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-100 text-center">
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="text-xs text-[#F39C12] hover:underline font-medium"
              >
                Voir toutes les notifications →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;